
// Two-Factor Authentication Settings Component
class TwoFactorSettings extends HTMLElement {
  constructor() {
    super();
    this.twoFactorEnabled = false;
    this.isLoading = true;
  }

  connectedCallback() {
    this.render();
    this.load2FAStatus().then(() => {
      this.attachEventListeners();
    });
  }

  async load2FAStatus() {
    try {
      // Use centralized API helper
      if (!window.API) {
        throw new Error('API not available');
      }

      const data = await window.API.request('/auth/2fa/status');
      this.twoFactorEnabled = data.twoFactorEnabled;
    } catch (error) {
      window.logger.error('Failed to load 2FA status:', error);
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  render() {
    if (this.isLoading) {
      this.innerHTML = `
        <div class="two-factor-settings">
          <div class="loading-state">
            <div class="spinner"></div>
            <div>Loading 2FA settings...</div>
          </div>
        </div>
      `;
      return;
    }

    this.innerHTML = `
      <div class="two-factor-settings">
        <div class="settings-header">
          <h3>🔐 Two-Factor Authentication</h3>
          <p class="settings-description">
            Add an extra layer of security to your account with two-factor authentication.
          </p>
        </div>

        <div class="settings-content">
          <div class="two-factor-status">
            <div class="status-indicator ${this.twoFactorEnabled ? 'enabled' : 'disabled'}">
              <span class="status-icon">${this.twoFactorEnabled ? '✅' : '❌'}</span>
              <span class="status-text">
                ${this.twoFactorEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <p class="status-description">
              ${this.twoFactorEnabled
    ? 'Your account is protected with two-factor authentication.'
    : 'Your account is not protected with two-factor authentication.'
}
            </p>
          </div>

          <div class="two-factor-actions">
            ${this.twoFactorEnabled ? this.renderDisableSection() : this.renderEnableSection()}
          </div>
        </div>

        <div class="alert-container" id="twoFactorAlertContainer"></div>

        <div class="settings-footer">
          <button class="btn btn-secondary" id="returnToSettingsBtn">
            ← Return to Settings
          </button>
        </div>
      </div>
    `;
  }

  renderEnableSection() {
    return `
      <div class="enable-2fa">
        <h4>Enable Two-Factor Authentication</h4>
        <p>Use an authenticator app like Google Authenticator or Authy to generate time-based codes.</p>
        <button class="btn btn-primary" id="enable2FABtn">
          <span class="btn-text">Setup 2FA</span>
          <span class="spinner hidden"></span>
        </button>
      </div>
    `;
  }

  renderDisableSection() {
    return `
      <div class="disable-2fa">
        <h4>Disable Two-Factor Authentication</h4>
        <p>You'll need to enter a verification code from your authenticator app to disable 2FA.</p>
        
        <div class="disable-form" id="disableForm" style="display: none;">
          <div class="form-group">
            <label class="form-label" for="disableToken">Verification Code</label>
            <input 
              type="text" 
              class="form-control" 
              id="disableToken" 
              placeholder="Enter 6-digit code"
              maxlength="6"
              pattern="[0-9]{6}"
              autocomplete="one-time-code"
              inputmode="numeric"
            />
            <div class="form-text">Enter the current code from your authenticator app</div>
          </div>
          <div class="form-actions">
            <button class="btn btn-danger" id="confirmDisableBtn">
              <span class="btn-text">Disable 2FA</span>
              <span class="spinner hidden"></span>
            </button>
            <button class="btn btn-outline" id="cancelDisableBtn">Cancel</button>
          </div>
        </div>

        <button class="btn btn-outline" id="showDisableFormBtn">
          Disable 2FA
        </button>
      </div>
    `;
  }

  attachEventListeners() {
    // Enable 2FA button
    const enableBtn = this.querySelector('#enable2FABtn');
    if (enableBtn) {
      enableBtn.addEventListener('click', () => this.setup2FA());
    }

    // Show disable form button
    const showDisableBtn = this.querySelector('#showDisableFormBtn');
    if (showDisableBtn) {
      showDisableBtn.addEventListener('click', () => this.showDisableForm());
    }

    // Cancel disable button
    const cancelBtn = this.querySelector('#cancelDisableBtn');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => this.hideDisableForm());
    }

    // Confirm disable button
    const confirmBtn = this.querySelector('#confirmDisableBtn');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => this.disable2FA());
    }

    // Return to settings button
    const returnBtn = this.querySelector('#returnToSettingsBtn');
    if (returnBtn) {
      returnBtn.addEventListener('click', () => {
        if (window.router) {
          window.router.navigate('/settings');
        }
      });
    }
  }

  async setup2FA() {
    const btn = this.querySelector('#enable2FABtn');
    const btnText = btn.querySelector('.btn-text');
    const spinner = btn.querySelector('.spinner');

    btn.disabled = true;
    btnText.classList.add('hidden');
    spinner.classList.remove('hidden');

    try {
      // Open the auth modal in 2FA setup mode
      const authModal = document.querySelector('auth-modal');
      if (authModal) {
        await authModal.open2FASetup();
        // Listen for successful 2FA setup
        authModal.addEventListener('auth-success', () => {
          this.twoFactorEnabled = true;
          this.render();
          this.attachEventListeners();
        });
      } else {
        window.logger.error('Auth modal not found in DOM');
        this.showAlert('Auth modal not available. Please refresh the page.', 'error');
      }
    } catch (error) {
      window.logger.error('2FA setup error:', error);
      this.showAlert('Failed to setup 2FA. Please try again.', 'error');
    } finally {
      btn.disabled = false;
      btnText.classList.remove('hidden');
      spinner.classList.add('hidden');
    }
  }

  showDisableForm() {
    const form = this.querySelector('#disableForm');
    const showBtn = this.querySelector('#showDisableFormBtn');

    form.style.display = 'block';
    showBtn.style.display = 'none';

    // Focus the input
    const input = this.querySelector('#disableToken');
    input.focus();
  }

  hideDisableForm() {
    const form = this.querySelector('#disableForm');
    const showBtn = this.querySelector('#showDisableFormBtn');

    form.style.display = 'none';
    showBtn.style.display = 'block';

    // Clear the input
    const input = this.querySelector('#disableToken');
    input.value = '';
  }

  async disable2FA() {
    const tokenInput = this.querySelector('#disableToken');
    const token = tokenInput.value.trim();

    if (!token || token.length !== 6) {
      this.showAlert('Please enter a valid 6-digit verification code.', 'error');
      return;
    }

    const btn = this.querySelector('#confirmDisableBtn');
    const btnText = btn.querySelector('.btn-text');
    const spinner = btn.querySelector('.spinner');

    btn.disabled = true;
    btnText.classList.add('hidden');
    spinner.classList.remove('hidden');

    try {
      // Use centralized API helper
      if (!window.API) {
        throw new Error('API not available');
      }

      await window.API.request('/auth/2fa/disable', {
        method: 'POST',
        body: JSON.stringify({ token })
      });

      this.showAlert('Two-factor authentication has been disabled successfully.', 'success');
      this.twoFactorEnabled = false;
      this.hideDisableForm();
      this.render();
      this.attachEventListeners();

    } catch (error) {
      window.logger.error('2FA disable error:', error);
      this.showAlert(error.message, 'error');
    } finally {
      btn.disabled = false;
      btnText.classList.remove('hidden');
      spinner.classList.add('hidden');
    }
  }

  showAlert(message, type = 'info') {
    const container = this.querySelector('#twoFactorAlertContainer');
    const alert = document.createElement('div');
    alert.className = `alert alert-${type}`;
    alert.textContent = message;

    container.innerHTML = '';
    container.appendChild(alert);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (alert.parentNode) {
        alert.parentNode.removeChild(alert);
      }
    }, 5000);
  }

  clearAlerts() {
    const container = this.querySelector('#twoFactorAlertContainer');
    if (container) {
      container.innerHTML = '';
    }
  }

}

customElements.define('two-factor-settings', TwoFactorSettings);
